import { InstagramOutlined, TikTokOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useRequest } from 'ahooks';
import { Badge, Col, Image, Row, Skeleton, Statistic } from 'antd';
import laurine from 'assets/laurine.jpg';
import Logo from 'assets/logos/logo.svg?react';
import Ribbon from 'assets/ribbon.svg?react';
import { AxiosError } from 'axios';
import MedicalProfileCard, { getMedicalProfileState } from 'components/cards/MedicalProfileCard';
import ModuleCard from 'components/cards/ModuleCard';
import OctobreRoseCard from 'components/cards/OctobreRoseCard';
import PreventionJourneyModule from 'components/cards/PreventionJourneyModule';
import RebrandingAnnouncementCard from 'components/cards/RebrandingAnnouncementCard';
import LinearCalendarChart from 'components/charts/LinearCalendarChart';
import ChatsList from 'components/chats/ChatsList';
import PageTopBar from 'components/navigation/PageTopBar';
import PartnerSection from 'components/PartnerSection';
import PostsList from 'components/PostsList';
import { Button, VisuallyHidden } from 'components/ui';
import dayjs from 'dayjs';
import { colors } from 'design';
import { useConversationsByType, useConversationsLoadingStatus } from 'hooks/useConversations';
import usePreferences from 'hooks/usePreferences';
import useScrollRestoration from 'hooks/useScrollRestoration';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ApiContentManager from 'services/api/ApiContentManager';
import ApiQuestionnaireManager from 'services/api/ApiQuestionnaireManager';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { QuestionnaireResponseDTO } from 'services/api/models/QuestionnaireDTO';
import { routes } from 'services/RouteService';
import { contentActions } from 'store/content';
import { selectSuggestedPosts } from 'store/content/selectors';
import { userActions } from 'store/users';
import { selectCurrentUser, selectHasAccessWithLicenseCode } from 'store/users/selectors';
import Drop from '../assets/drop.svg?react';

const useStyles = createUseStyles({
  pageContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: colors.surface.page,
  },
  container: {
    backgroundColor: colors.surface.page,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    maxWidth: 600,
    width: '100%',
    padding: 16,
    paddingBottom: 24,
    overflowY: 'auto',

    '& .rce-citem-avatar': {
      paddingLeft: 0,
    },
    '& .rce-citem-body': {
      paddingRight: 0,
    },
  },
  welcomeMessage: {
    width: '100%',
    textAlign: 'center',
    marginTop: 32,
    color: colors.brand.accentStrong,
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  skeletonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  trackingTitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  graphLabel: {
    margin: 0,
    color: colors.text.disabled,
  },
  trackingGraphContainer: {
    marginBottom: 16,
    overflowX: 'scroll',
    overflowY: 'hidden',
  },
  nextPeriodDatesVariation: {
    color: colors.text.disabled,
    fontWeight: 'normal',
    marginLeft: 8,
  },
  caregiverStatsContainer: {
    marginBottom: 8,
  },
  nextPeriodPredictionContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 32,
  },
  nextPeriodDescription: {
    marginBottom: 8,
  },
  nextPeriodPrediction: {
    marginTop: 0,
    fontSize: 17,
    fontWeight: 700,
    textAlign: 'right',
    '& svg': {
      color: colors.dataViz.veryBad,
      marginRight: 4,
    },
  },
  nextPeriodDatesPrediction: {
    marginBottom: 8,
  },
  socialLinksTitle: {
    marginTop: 32,
    marginBottom: 8,
    fontSize: 16,
    fontWeight: 700,
    textAlign: 'center',
    color: colors.text.tertiary,
  },
  socialLinksDescription: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: 13,
    fontWeight: 'normal',
    textAlign: 'center',
    color: colors.text.tertiary,
  },
  socialLinksContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 24,
    marginBottom: 16,
    paddingTop: 8,
  },
  socialLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    transition: 'opacity 0.2s',
    '&:hover': {
      opacity: 0.7,
    },
  },
  socialIcon: {
    fontSize: 32,
    lineHeight: 1,
    color: colors.brand.highlight,
  },

  // Explainer-v2 variant of the no-messages card
  referentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 8,
    marginBottom: 16,
  },
  referentAvatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
    border: `2px solid ${colors.brand.accentSubtle}`,
  },
  referentName: {
    margin: 0,
    fontWeight: 700,
    fontSize: '0.95rem',
    color: colors.text.primary,
    lineHeight: 1.2,
  },
  referentRole: {
    margin: 0,
    fontSize: '0.8rem',
    color: colors.text.tertiary,
    lineHeight: 1.2,
  },
  referentTagline: {
    //   fontWeight: 600,
    fontSize: '0.9rem',
    margin: '0 0 8px',
    color: colors.text.primary,
  },
  compactSpecialtiesScroller: {
    position: 'relative',
    marginBottom: 12,
    // Without this the strip below cannot scroll: `min-width: auto` on a flex
    // item resolves to its content width, so the grid widens its ancestors
    // instead of overflowing them, and the whole document ends up wider than
    // the viewport. Invisible while the page was white — the chrome bars simply
    // stopped short of the edge — and obvious the moment the page has a colour.
    minWidth: 0,
  },
  compactSpecialtiesGrid: {
    display: 'grid',
    gridTemplateRows: 'repeat(2, auto)',
    gridAutoFlow: 'column',
    gap: '4px 6px',
    minWidth: 0,
    overflowX: 'auto',
    paddingBottom: 2,
    paddingRight: 24,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  compactSpecialtiesFade: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 2,
    width: 36,
    background: `linear-gradient(to right, transparent, ${colors.surface.base})`,
    pointerEvents: 'none',
  },
  compactSpecialtyTag: {
    fontSize: '0.72rem',
    padding: '2px 8px',
    borderRadius: 20,
    backgroundColor: colors.brand.accentSubtle,
    // Primary ink, not a recessive tier: this chip's fill is `accentSubtle`,
    // which the primary/accent swap moved from a pale mint to teal 100. The
    // metadata tier sits at 3.97:1 on it and even the secondary tier only
    // reaches 4.50 — no margin at all for an 11.5px label.
    color: colors.text.primary,
    whiteSpace: 'nowrap' as const,
  },
});

export default function Home() {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const styles = useStyles();
  const currentUser = useSelector(selectCurrentUser);
  const [shouldDisplaySurvey, setShouldDisplaySurvey] = useState(false);
  const areConversationsLoading = useConversationsLoadingStatus() === 'LOADING';
  const conversations = useConversationsByType('assigned');
  const unassignedConversations = useConversationsByType('unassigned');
  const myRequestsConversations = useConversationsByType('myRequest');
  const waitingForReassignmentConversations = useConversationsByType('waitingForReassignment');
  const hasAccessWithLicenseCode = useSelector(selectHasAccessWithLicenseCode);

  // Calculate total unread messages across all conversations
  const totalUnreadMessages = useMemo(() => {
    return conversations.reduce((total, conv) => total + (conv.number_of_unread_messages || 0), 0);
  }, [conversations]);

  const { data: questionnaireResponse } = useQuery<QuestionnaireResponseDTO | null>({
    queryKey: ['questionnaire', 'myResponse', currentUser?.id],
    queryFn: () =>
      ApiQuestionnaireManager.getMyResponse().catch(err => {
        if (err instanceof AxiosError && err.response?.status === 404) return null;
        throw err;
      }),
    enabled: Boolean(!currentUser?.is_caregiver),
    staleTime: 1000 * 60 * 5,
  });

  const { value: shouldAskForPeriodPreference } = usePreferences(Keys.TRACKING_SURVEY_SHOULD_ASK_FOR_PERIOD);
  const isPeriodTrackingEnabled = shouldAskForPeriodPreference !== false;

  const {
    getDisplayedDays,
    getStatusValueForDisplayedDays,
    cycleStats,
    hasTrackingBeenUsed,
    hasTrackingForToday,
    loading: loadingTracking,
    daysUntilNextPeriod,
    nextPeriodPrediction,
  } = useSymptomTrackingData();
  const displayedDays = getDisplayedDays(dayjs());
  const generalStateValues = getStatusValueForDisplayedDays('general_state', displayedDays);

  const { predictionLabel, predictionValue } = nextPeriodPrediction;

  const suggestedPosts = useSelector(selectSuggestedPosts);

  const { run: loadPosts, loading: isLoadingPosts } = useRequest(() => ApiContentManager.getPosts(), {
    manual: true,
    cacheKey: `content.suggestedPosts`,
    cacheTime: 1000 * 60 * 10, // 10 minutes
    onSuccess: data => {
      if (!suggestedPosts) {
        // storing suggested posts in the store to avoid fetching them again
        // as they are shuffled on the backend. This way, we can keep
        // the same order for a given session of a user.
        // This makes it feel more natural to the user, and also helps
        // when coming back to a list of articles from a specific article
        // as it keeps the same position in the list of suggested articles.
        dispatch(contentActions.setSuggestedPosts(data));
      }
    },
  });

  useEffect(() => {
    if (!suggestedPosts) {
      loadPosts();
    }
  }, [suggestedPosts, loadPosts]);

  const scrollableRef = useRef<HTMLDivElement>(null);
  useScrollRestoration(scrollableRef);

  const trackingGraphRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (trackingGraphRef.current) {
      trackingGraphRef.current.scrollLeft = trackingGraphRef.current.scrollWidth;
    }
  }, [trackingGraphRef, generalStateValues]);

  useEffect(() => {
    if (currentUser && currentUser.should_ask_for_nps && !currentUser.is_caregiver) {
      setShouldDisplaySurvey(true);
      ApiUsersManager.acknowledgeNPS();

      // update the current user locally to disable NPS
      dispatch(userActions.disableNPSSurvey());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const skeleton = (
    <div className={styles.skeletonContainer}>
      <Skeleton.Input block active style={{ height: 100 }} />
      <Skeleton.Input block active />
    </div>
  );

  const messagesCardForCaregivers = (
    <>
      <Row className={styles.caregiverStatsContainer}>
        <Col span={7}>
          <Statistic
            title={t('home.messages.waitingForMe')}
            value={unassignedConversations.length + myRequestsConversations.length}
            precision={0}
          />
        </Col>
        <Col span={7}>
          <Statistic title={t('home.messages.inProgress')} value={conversations.length} precision={0} />
        </Col>
        <Col span={10}>
          <Statistic
            title={t('home.messages.waitingForReassignment')}
            value={waitingForReassignmentConversations.length}
            precision={0}
          />
        </Col>
      </Row>
      <Button variant="primary" block size="lg" onClick={() => navigate(routes.messagingHome.route)}>
        {t('home.messages.goToMessages')}
      </Button>
    </>
  );

  const COMPACT_SPECIALTIES = [
    { emoji: '🌸', key: 'chats.explainerV2.specialties.gynecologists' },
    { emoji: '🌿', key: 'chats.explainerV2.specialties.midwives' },
    { emoji: '🤲', key: 'chats.explainerV2.specialties.physiotherapists' },
    { emoji: '🥑', key: 'chats.explainerV2.specialties.nutritionists' },
    { emoji: '💬', key: 'chats.explainerV2.specialties.psychologists' },
    { emoji: '👶', key: 'chats.explainerV2.specialties.childcareNurses' },
    { emoji: '🦴', key: 'chats.explainerV2.specialties.osteopaths' },
    { emoji: '👩‍⚕️', key: 'chats.explainerV2.specialties.nurses' },
    { emoji: '🌱', key: 'chats.explainerV2.specialties.naturopaths' },
    { emoji: '🧘', key: 'chats.explainerV2.specialties.sophrologists' },
  ];

  const messagesCardWithoutMessages = (
    <>
      <div className={styles.referentRow}>
        <img src={laurine} alt="Laurine" className={styles.referentAvatar} />
        <div>
          <p className={styles.referentName}>{t('home.messages.referralName')}</p>
          <p className={styles.referentRole}>{t('home.messages.referralTagline')}</p>
        </div>
      </div>
      <p className={styles.referentTagline}>{t('home.messages.tagline')}</p>
      <div className={styles.compactSpecialtiesScroller}>
        <div className={styles.compactSpecialtiesGrid}>
          {COMPACT_SPECIALTIES.map(s => (
            <span key={s.key} className={styles.compactSpecialtyTag}>
              {s.emoji} {t(s.key)}
            </span>
          ))}
        </div>
        <div className={styles.compactSpecialtiesFade} />
      </div>
      <Button variant="primary" block size="lg" onClick={() => navigate(routes.messagingHome.route)}>
        {t('home.messages.callToAction')}
      </Button>
    </>
  );

  const messagesCardWithMessages = (
    <>
      <ChatsList conversations={conversations} title={null} readOnlyConvs={false} maximumConversations={1} />
      <Button
        variant="primary"
        block
        size="lg"
        testId="home-open-messaging-button"
        onClick={() => navigate(routes.messagingHome.route)}
      >
        <span>
          {t('home.messages.goToMessages')}
          <Badge
            data-testid="unread-messages-badge"
            color="white"
            count={totalUnreadMessages}
            style={{ color: colors.brand.accentStrong, backgroundColor: 'white' }}
            offset={[8, -4]}
          />
        </span>
      </Button>
    </>
  );
  const trackingCardWithoutData = (
    <>
      <p>{t('home.tracking.description')}</p>
      <div className={styles.trackingGraphContainer}>
        <Image src={require('../assets/home-tracking-example.png')} preview={false} />
      </div>

      <Button variant="primary" block size="lg" onClick={() => navigate(routes.symptomTracking.route)}>
        {t('home.tracking.fillSurvey')}
      </Button>
    </>
  );

  const periodPrediction = daysUntilNextPeriod && (
    <div>
      <p className={styles.graphLabel}>{predictionLabel}</p>
      <p className={styles.nextPeriodPrediction}>
        <Drop fill={colors.dataViz.veryBad} height={11} width={11} />
        {predictionValue}
        {cycleStats.roundedStandardDeviation >= 1 && (
          <span className={styles.nextPeriodDatesVariation}>± {cycleStats.roundedStandardDeviation} jours</span>
        )}
      </p>
    </div>
  );

  const trackingCardWithData = (
    <>
      {isPeriodTrackingEnabled && daysUntilNextPeriod && (
        <div className={styles.nextPeriodPredictionContainer}>
          <></>
        </div>
      )}
      <div className={styles.trackingTitleContainer}>
        {hasTrackingBeenUsed && <p className={styles.graphLabel}>{t('home.tracking.graphLabel')}</p>}
      </div>
      <div className={styles.trackingGraphContainer} ref={trackingGraphRef}>
        <LinearCalendarChart dayLabels={displayedDays} dayValues={generalStateValues} range={[0, 4]} />
      </div>
      {hasTrackingForToday ? (
        <Button variant="primary" block size="lg" onClick={() => navigate(routes.symptomTracking.route)}>
          {t('home.tracking.goToTracking')}
        </Button>
      ) : (
        <Button variant="primary" block size="lg" onClick={() => navigate(routes.symptomTracking.route)}>
          {t('home.tracking.fillSurvey')}
        </Button>
      )}
    </>
  );

  const medicalProfileState = getMedicalProfileState(questionnaireResponse);
  const isUserEligibleForMedicalProfile = !currentUser?.is_caregiver && hasAccessWithLicenseCode;
  const showMedicalProfileAtTop =
    isUserEligibleForMedicalProfile &&
    questionnaireResponse !== undefined &&
    (medicalProfileState === 'not_started' ||
      medicalProfileState === 'in_progress' ||
      medicalProfileState === 'pending_review' ||
      medicalProfileState === 'in_review');
  const showMedicalProfileBelowTracking = isUserEligibleForMedicalProfile && medicalProfileState === 'reviewed';

  return (
    <div className={styles.pageContainer}>
      <PageTopBar
        title={
          <>
            {/* The wordmark *is* the heading, so the heading needs the words in
                it. An <svg> contributes no text, and `aria-label` on a bare
                <svg> is widely ignored, so without this the page heading is
                empty for screen readers and for the document outline. */}
            <span className={styles.brandRow}>
              <Logo
                aria-hidden="true"
                focusable="false"
                fill={colors.brand.accent}
                style={{
                  height: 24,
                  width: 'auto',
                  display: 'block',
                  marginTop: 4,
                  marginBottom: 4,
                  marginLeft: 8,
                }}
              />
              <Ribbon aria-hidden="true" width={24} height={24} style={{ marginBottom: 5 }} />
            </span>
            <VisuallyHidden>Gynger</VisuallyHidden>
          </>
        }
      />
      <div className={styles.container} ref={scrollableRef}>
        <h2 className={styles.welcomeMessage}>{t('home.greeting', { firstName: currentUser?.first_name })}</h2>

        <RebrandingAnnouncementCard />

        {showMedicalProfileAtTop && <MedicalProfileCard response={questionnaireResponse} />}

        <ModuleCard title={t('home.messages.title')}>
          {areConversationsLoading
            ? skeleton
            : currentUser?.is_caregiver
              ? messagesCardForCaregivers
              : conversations.length > 0
                ? messagesCardWithMessages
                : messagesCardWithoutMessages}
        </ModuleCard>

        <OctobreRoseCard openedOnPage="home" />

        <ModuleCard title={t('home.tracking.title')} topRightContent={isPeriodTrackingEnabled && periodPrediction}>
          {loadingTracking ? skeleton : hasTrackingBeenUsed ? trackingCardWithData : trackingCardWithoutData}
        </ModuleCard>

        {showMedicalProfileBelowTracking && <MedicalProfileCard response={questionnaireResponse} />}

        {posthog.getFeatureFlag('prevention-program') === 'opt-in' && <PreventionJourneyModule />}

        <ModuleCard title={t('home.info.title')}>
          <Row>
            {isLoadingPosts ? (
              <div>
                <Skeleton.Input block active style={{ height: 64, marginTop: 16 }} />
                <Skeleton.Input block active style={{ height: 64, marginTop: 16 }} />
                <Skeleton.Input block active style={{ height: 64, marginTop: 16 }} />
              </div>
            ) : (
              <PostsList
                posts={suggestedPosts ?? []}
                limitPosts={4}
                posthogOnArticleOpenExtras={{ opened_on_page: 'home' }}
              />
            )}
          </Row>
        </ModuleCard>
        {shouldDisplaySurvey && <span id="show-posthog-survey" />}

        {posthog.isFeatureEnabled('homepage-social-links-display') && (
          <>
            <div>
              <h3 className={styles.socialLinksTitle}>{t('home.social.title')}</h3>
              <p className={styles.socialLinksDescription}>{t('home.social.description')}</p>
            </div>
            <div className={styles.socialLinksContainer}>
              <a
                href={t('social.instagram')}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
                aria-label="Instagram"
                onClick={() => {
                  posthog.capture('home.social_link_clicked', {
                    platform: 'instagram',
                  });
                }}
              >
                <span className={styles.socialIcon}>
                  <InstagramOutlined />
                </span>
              </a>
              <a
                href={t('social.tiktok')}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
                aria-label="TikTok"
                onClick={() => {
                  posthog.capture('home.social_link_clicked', {
                    platform: 'tiktok',
                  });
                }}
              >
                <span className={styles.socialIcon}>
                  <TikTokOutlined />
                </span>
              </a>
            </div>
          </>
        )}
        <PartnerSection />
      </div>
    </div>
  );
}
